import { useMutation, useQueryClient } from '@tanstack/react-query'
import { saveGoalsMutation } from '../data/goalsApi.js'
import { useHistoryStore } from '../store/historyStore.js'
import { useArchiveViewStore } from '../store/archiveViewStore.js'

/**
 * useGoalsCrud — add/delete goals and sub-goals, plus pillar-level
 * metadata edits.
 *
 * Concurrency model:
 *   Each operation goes through /goals/mutate, which does a
 *   read-modify-write server-side for that specific structural change.
 *   Two people adding goals to different pillars (or even the same
 *   pillar) at the same time no longer clobber each other — each
 *   addGoal is applied in order to the latest KV state.
 *
 *   Previously this file POSTed the ENTIRE goals array for every
 *   structural change, which meant any concurrent edit on an
 *   unrelated goal/field could be silently lost. Fixed 2026-04-24.
 *
 * Returns { addGoal, removeGoal, addSubgoal, removeSubgoal, updatePillar }.
 * Each call pushes an undo snapshot first so ⌘Z can revert.
 */
export function useGoalsCrud() {
  const queryClient = useQueryClient()

  const pushSnapshot = (label) => {
    useHistoryStore
      .getState()
      .pushSnapshot(
        label,
        queryClient.getQueryData(['goals']),
        queryClient.getQueryData(['meta'])
      )
  }

  // Archive-view guard. Every CRUD method dispatches via this helper;
  // if we're viewing a past week, swallow the call so no writes leak
  // into the live goals blob.
  const blockedByArchive = () => {
    if (useArchiveViewStore.getState().active) {
      console.warn('[archive-view] goals CRUD blocked while viewing a past week')
      return true
    }
    return false
  }

  // Shared optimistic-cache updater wrapper. `localUpdater` runs against
  // the local cache for the instant-UI effect, then we POST the
  // server-side op and let the server's return value replace the cache.
  const mutation = useMutation({
    mutationFn: async ({ payload }) => saveGoalsMutation(payload),
    onMutate: async ({ localUpdater, label }) => {
      await queryClient.cancelQueries({ queryKey: ['goals'] })
      const previous = queryClient.getQueryData(['goals'])
      pushSnapshot(label)
      queryClient.setQueryData(['goals'], (old) => localUpdater(old || []))
      return { previous }
    },
    onSuccess: (serverGoals) => {
      if (Array.isArray(serverGoals)) {
        queryClient.setQueryData(['goals'], serverGoals)
      }
    },
    onError: (err, vars, ctx) => {
      if (ctx?.previous) queryClient.setQueryData(['goals'], ctx.previous)
    },
  })

  // ── Pillar-level metadata (description etc.) ─────────────────────
  const updatePillar = ({ pillarName, field, value }) => {
    if (blockedByArchive()) return
    mutation.mutate({
      label: `Edit ${pillarName} ${field}`,
      payload: { op: 'updatePillar', pillarName, field, value },
      localUpdater: (data) =>
        data.map((p) =>
          p.pillar === pillarName ? { ...p, [field]: value } : p
        ),
    })
  }

  // ── Goal CRUD ────────────────────────────────────────────────────
  const addGoal = ({ pillarName, goal }) => {
    if (blockedByArchive()) return
    mutation.mutate({
      label: `Add goal to ${pillarName}`,
      payload: { op: 'addGoal', pillarName, goal },
      localUpdater: (data) =>
        data.map((p) =>
          p.pillar === pillarName
            ? { ...p, goals: [...(p.goals || []), goal] }
            : p
        ),
    })
  }

  const removeGoal = ({ pillarName, goalIndex }) => {
    if (blockedByArchive()) return
    mutation.mutate({
      label: `Remove goal from ${pillarName}`,
      payload: { op: 'removeGoal', pillarName, goalIndex },
      localUpdater: (data) =>
        data.map((p) =>
          p.pillar === pillarName
            ? {
                ...p,
                goals: (p.goals || []).filter((_, i) => i !== goalIndex),
              }
            : p
        ),
    })
  }

  // Reorder a goal within its pillar by moving it from `fromIndex` to
  // `toIndex`. The server splices in place atomically (read-modify-write),
  // so concurrent reorders on different pillars don't collide. Within
  // the same pillar, last writer wins on the move sequence — acceptable
  // since reorders are rare and visible.
  const reorderGoal = ({ pillarName, fromIndex, toIndex }) => {
    if (blockedByArchive()) return
    if (fromIndex === toIndex) return
    mutation.mutate({
      label: `Reorder goals in ${pillarName}`,
      payload: { op: 'reorderGoal', pillarName, fromIndex, toIndex },
      localUpdater: (data) =>
        data.map((p) => {
          if (p.pillar !== pillarName) return p
          const goals = [...(p.goals || [])]
          if (
            fromIndex < 0 ||
            fromIndex >= goals.length ||
            toIndex < 0 ||
            toIndex >= goals.length
          ) {
            return p
          }
          const [moved] = goals.splice(fromIndex, 1)
          goals.splice(toIndex, 0, moved)
          return { ...p, goals }
        }),
    })
  }

  // ── Sub-goal CRUD ────────────────────────────────────────────────
  const addSubgoal = ({ pillarName, goalIndex, subgoal }) => {
    if (blockedByArchive()) return
    mutation.mutate({
      label: `Add sub-goal`,
      payload: { op: 'addSubgoal', pillarName, goalIndex, subgoal },
      localUpdater: (data) =>
        data.map((p) =>
          p.pillar === pillarName
            ? {
                ...p,
                goals: (p.goals || []).map((g, i) =>
                  i === goalIndex
                    ? { ...g, subgoals: [...(g.subgoals || []), subgoal] }
                    : g
                ),
              }
            : p
        ),
    })
  }

  const removeSubgoal = ({ pillarName, goalIndex, subgoalIndex }) => {
    if (blockedByArchive()) return
    mutation.mutate({
      label: `Remove sub-goal`,
      payload: { op: 'removeSubgoal', pillarName, goalIndex, subgoalIndex },
      localUpdater: (data) =>
        data.map((p) =>
          p.pillar === pillarName
            ? {
                ...p,
                goals: (p.goals || []).map((g, i) =>
                  i === goalIndex
                    ? {
                        ...g,
                        subgoals: (g.subgoals || []).filter(
                          (_, j) => j !== subgoalIndex
                        ),
                      }
                    : g
                ),
              }
            : p
        ),
    })
  }

  return {
    addGoal,
    removeGoal,
    reorderGoal,
    addSubgoal,
    removeSubgoal,
    updatePillar,
    isLoading: mutation.isPending,
  }
}
